import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";
import type { MosaicNode } from "react-mosaic-component";
import { useToast } from "@/components/ui/use-toast";
import { useGetUserDataFromToken } from "@/hooks/useGetUserDataFromToken";
import type { ViewId } from "@/shared/types/common";
import { TabsStateProvider } from "./TabsStateProvider";
import type {
	Tabs,
	TabsContextProps,
	TabsState,
	WidgetState,
	Widgets,
} from "./types";

const TabsContext = createContext<TabsContextProps | undefined>(undefined);

export const TabsProvider = ({ children }: { children: ReactNode }) => {
	const [, setState] = useState<Omit<Tabs, "userId">>();
	const userData = useGetUserDataFromToken();
	const { toast } = useToast("tabs");
	const userId = userData?.username;

	let tabsStateProvider = TabsStateProvider.getDefault();

	useEffect(() => {
		const test = async () => {
			tabsStateProvider = await TabsStateProvider.getProvider(userId);
			setState(tabsStateProvider.getState());
		};
		test();
	}, [userId, tabsStateProvider]);

	const setActiveTab = (tabId: string): void => {
		tabsStateProvider.setActiveTab(tabId);
		setState(tabsStateProvider.getState());
	};

	const getActiveTab = (): string => {
		return tabsStateProvider.getActiveTab();
	};

	const setTabName = (
		tabId: string,
		tabName: string,
		newName: string,
	): void => {
		if (tabName === newName) return;

		tabsStateProvider.setTabName(tabId, newName);
		setState(tabsStateProvider.getState());
		toast({ title: `${tabName} renamed to ${newName}` });
	};

	const setTabLayout = (layout: MosaicNode<ViewId> | null): void => {
		tabsStateProvider.setTabLayout(layout);
		setState(tabsStateProvider.getState());
	};

	const getTabLayout = (): MosaicNode<ViewId> | null => {
		return tabsStateProvider.getTabLayout();
	};

	const setTabWidgetsState = (widgets: Widgets): void => {
		tabsStateProvider.setTabWidgetsState(widgets);
		setState(tabsStateProvider.getState());
	};

	const getTabWidgetsState = (): Widgets => {
		return tabsStateProvider.getTabWidgetsState();
	};

	const setWidgetState = (
		widgetId: ViewId,
		data: Partial<WidgetState>,
		shouldRerender = true,
	): void => {
		tabsStateProvider.setWidgetState(widgetId, data);
		if (shouldRerender) {
			setState(tabsStateProvider.getState());
		}
	};

	const getWidgetState = (widgetId: ViewId): WidgetState | undefined => {
		return tabsStateProvider.getWidgetState(widgetId);
	};

	const getTabs = (): TabsState => {
		return tabsStateProvider.getState().tabs;
	};

	const addTab = (): void => {
		const name = tabsStateProvider.addTab();
		setState(tabsStateProvider.getState());
		toast({ title: `${name} - Created` });
	};

	const closeTab = (tabId: string): void => {
		tabsStateProvider.closeTab(tabId);
		setState(tabsStateProvider.getState());
	};

	const resetState = (): void => {
		if (userId) {
			tabsStateProvider.resetState(userId);
			setState(tabsStateProvider.getState());
		}
	};

	return (
		<TabsContext.Provider
			value={{
				getActiveTab,
				setActiveTab,
				setTabName,
				setTabLayout,
				getTabLayout,
				setTabWidgetsState,
				getTabWidgetsState,
				setWidgetState,
				getWidgetState,
				getTabs,
				addTab,
				closeTab,
				resetState,
			}}
		>
			{children}
		</TabsContext.Provider>
	);
};

export const useTabs = () => {
	const context = useContext(TabsContext);
	if (!context) {
		throw new Error("useTabs must be used within a TabsProvider");
	}
	return context;
};
